import { useState, type FormEvent } from 'react'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useDbLogin, errorMessages } from '../hooks/useDbLogin'
import styles from './DbLoginForm.module.css'

interface DbLoginFormProps {
  adminLogin?: boolean
  usernameLabel?: string
  usernamePlaceholder?: string
  submitLabel?: string
  redirectUri?: string
}

export function DbLoginForm({
  adminLogin = false,
  usernameLabel = 'Email address',
  usernamePlaceholder = 'name@company.com',
  submitLabel = 'Sign in',
  redirectUri = '/',
}: DbLoginFormProps) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const { login, submitting, errorType } = useDbLogin({ adminLogin, redirectUri })

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    await login({ username, password })
  }

  return (
    <div className={styles.form}>
      <AnimatePresence>
        {errorType && (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className={styles.errorBanner}>
              <p className={styles.errorTitle}>{errorMessages[errorType].title}</p>
              <p className={styles.errorDesc}>{errorMessages[errorType].desc}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit} noValidate className={styles.form}>
        {/* Username */}
        <div className={styles.field}>
          <label htmlFor="username" className={styles.label}>
            {usernameLabel}
          </label>
          <div className={styles.inputWrapper}>
            <input
              id="username"
              type="text"
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={submitting}
              placeholder={usernamePlaceholder}
              className={[styles.input, errorType ? styles.inputError : ''].join(' ')}
            />
          </div>
        </div>

        {/* Password */}
        <div className={styles.field}>
          <label htmlFor="password" className={styles.label}>
            Password
          </label>
          <div className={styles.inputWrapper}>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              placeholder="••••••••"
              className={[
                styles.input,
                styles.inputPassword,
                errorType ? styles.inputError : '',
              ].join(' ')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              disabled={submitting}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className={styles.toggleBtn}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting || !username.trim() || !password.trim()}
          className={styles.submitBtn}
        >
          {submitting ? (
            <>
              <Loader2 size={18} className={styles.spinner} />
              Authenticating…
            </>
          ) : (
            submitLabel
          )}
        </button>
      </form>
    </div>
  )
}
